import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Truck } from 'lucide-react';

const AdminDeliveryZones = () => {
  const { authAxios } = useAuth();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', fee: 0, active: true });

  useEffect(() => {
    fetchZones();
    // eslint-disable-next-line
  }, []);

  const fetchZones = async () => {
    try {
      const response = await authAxios.get('/admin/delivery-zones');
      setZones(response.data);
    } catch {
      toast.error('Erro ao carregar zonas');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', fee: 0, active: true });
    setDialogOpen(true);
  };

  const openEdit = (zone) => {
    setEditing(zone);
    setFormData({ name: zone.name, fee: zone.fee, active: zone.active });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await authAxios.put(`/admin/delivery-zones/${editing.id}`, formData);
        toast.success('Zona atualizada!');
      } else {
        await authAxios.post('/admin/delivery-zones', formData);
        toast.success('Zona criada!');
      }
      setDialogOpen(false);
      fetchZones();
    } catch {
      toast.error('Erro ao salvar zona');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta zona?')) return;
    try {
      await authAxios.delete(`/admin/delivery-zones/${id}`);
      toast.success('Zona deletada!');
      fetchZones();
    } catch {
      toast.error('Erro ao deletar zona');
    }
  };

  const toggleActive = async (zone) => {
    try {
      await authAxios.put(`/admin/delivery-zones/${zone.id}`, { active: !zone.active });
      fetchZones();
    } catch {
      toast.error('Erro ao atualizar');
    }
  };

  if (loading) return <div className="text-center py-10 text-[#5C4247]">Carregando zonas...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2
          className="text-2xl font-bold text-[#291014]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="zones-section-title"
        >
          Zonas de Entrega ({zones.length})
        </h2>
        <Button
          onClick={openCreate}
          className="bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full"
          data-testid="add-zone-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Zona
        </Button>
      </div>

      <p className="text-sm text-[#5C4247] bg-blue-50 border border-blue-200 rounded-lg p-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
        💡 Cadastre os bairros que você atende e a respectiva taxa de entrega. Use &quot;Retirar na loja&quot; com taxa 0 para clientes que vão retirar.
      </p>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {zones.map((zone) => (
          <div
            key={zone.id}
            className={`bg-white rounded-2xl p-5 border border-[#F3E8E3] ${
              !zone.active ? 'opacity-60' : ''
            }`}
            data-testid={`zone-card-${zone.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-[#EA580C]" />
                <h3
                  className="font-bold text-[#291014]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid={`zone-name-${zone.id}`}
                >
                  {zone.name}
                </h3>
              </div>
              <Switch
                checked={zone.active}
                onCheckedChange={() => toggleActive(zone)}
                data-testid={`zone-active-switch-${zone.id}`}
              />
            </div>
            <p
              className="text-2xl font-bold text-[#7F1D1D] mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid={`zone-fee-${zone.id}`}
            >
              {zone.fee === 0 ? 'Grátis' : `R$ ${zone.fee.toFixed(2)}`}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEdit(zone)}
                className="flex-1"
                data-testid={`edit-zone-${zone.id}`}
              >
                <Edit className="h-3 w-3 mr-1" />
                Editar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(zone.id)}
                className="text-red-600 hover:bg-red-50"
                data-testid={`delete-zone-${zone.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="zone-dialog-title">
              {editing ? 'Editar Zona' : 'Nova Zona de Entrega'}
            </DialogTitle>
            <DialogDescription>
              Cadastre uma região e a respectiva taxa de entrega para seus clientes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome do Bairro/Região *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex: Centro, Beira-Mar..."
                data-testid="zone-name-input"
              />
            </div>
            <div>
              <Label>Taxa de Entrega (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.fee}
                onChange={(e) => setFormData({ ...formData, fee: parseFloat(e.target.value) || 0 })}
                required
                data-testid="zone-fee-input"
              />
              <p className="text-xs text-[#5C4247] mt-1">Use 0 para entrega gratuita ou retirada na loja</p>
            </div>
            <div className="flex items-center justify-between bg-[#FAF8F5] rounded-lg p-3">
              <Label>Zona ativa para clientes</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(v) => setFormData({ ...formData, active: v })}
                data-testid="zone-active-toggle"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#7F1D1D] hover:bg-[#991B1B] text-white rounded-full"
              data-testid="save-zone-button"
            >
              {editing ? 'Salvar Alterações' : 'Criar Zona'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDeliveryZones;
